import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import MapGpsPicker from '../MapGpsPicker';
import PhotoPicker from '../PhotoPicker';
import QRCodeLabel from '../QRCodeLabel';
import FieldError, { errorIdFor, invalidBorder, invalidProps } from '../FieldError';
import { localToday } from '../SlantStoragePanel';
import { focusElement } from '../../utils/useFocusOnOpen';
import { uploadPhotos } from '../../utils/uploadPhotos';
import { hasErrors, useFieldErrors, validationMessage } from '../../utils/formValidation';
import BankStockFields from './BankStockFields';
import {
  CONFIDENCE, Field, GROWTH_PATTERNS, PlateFields, RANKS, SIZES, STAGES, SUBSTRATES, fieldProps,
} from './IntakeFields';
import {
  DESCRIPTION_FIELDS, EMPTY_FORM, EMPTY_PLATE_SUB, SOURCES, SOURCE_TYPES, TISSUE_CONDITIONS, bankStockBody,
  bankStockErrors, defaultBankStock, externalBody, generateQrUid, plateBody, protocolToRegister, retagTubes,
  sourceErrors, sourcesFor, stepFailureReason, tubeCode,
} from './intakeSource';
import {
  btnStyle, errStyle, formStyle, fullRow, gridStyle, hintStyle, inputStyle, labelStyle, sectionHeading,
  subFormStyle,
} from './styles';

/*
 * An isolate's intake ("קליטת תבדיד"): something that came from outside --
 * a raw specimen, a live culture, bank stock, or only a report -- recorded
 * as an external collection, with the isolate the server mints for it.
 *
 * The first question is what arrived (intakeSource.js). Nothing else shows
 * until it is answered: the answer decides the fields that follow, and where
 * the isolate starts -- awaiting isolation, as a lab culture, or already in
 * the bank. Who it came from, where and when, the identification, notes and
 * photos are asked for every type.
 *
 * The form owns its state, its checks and its requests:
 *
 *   1. POST /external -- the record, and the isolate (`specimen` in its answer)
 *   2. the photos, attached to the record before anything else can fail
 *   3. for a culture POST /plates; for bank stock POST /slants/external-intake
 *      with the tubes' place, which banks every tube or registers none
 *
 * From step 1 on the record exists whatever happens, and nothing rolls back.
 * Sending the whole form again would make a second intake, so it never is:
 *
 *   - a culture whose plate failed closes, the failure handed to onDone as a
 *     warning; the intake waits in the hub's "awaiting the lab", where its
 *     record (IntakeRecordDrawer) can create its plate again.
 *   - bank stock whose tubes were refused STAYS OPEN, on its tubes only: the
 *     record is said to be saved, the refusal is said beside the tubes, and
 *     "Store the tubes" resends only POST /slants/external-intake for the
 *     record already made -- so a tube code, well, place or preservation
 *     record typed once is fixed, not typed again. Closing instead (its own
 *     button, Cancel, or the page's close through `ref`) hands onDone the
 *     refusal as a warning that says how to bank the tubes later.
 *
 * Props:
 *   user       the signed-in user: who received it, and who may register lab
 *              material (a culture or bank stock needs a lab writer's role)
 *   protocols  the media list (GET /protocols), for the plate and the tubes
 *   onDone({ external_id, specimen, sourceType, warnings })
 *              after the record was made; warnings: [{ kind: 'step' |
 *              'photos', message }], said in the reader's language
 *   onCancel   closes the form without saving (a Cancel button when given)
 *   onBusyChange(busy)
 *              a submission started (true) or ended (false). The page keeps
 *              its own ways of closing the form shut meanwhile: the request
 *              is already on its way, and closing would only hide its answer.
 *   onSettled()
 *              a submission that wrote something finished -- called even when
 *              the form was taken away mid-request and onDone no longer is:
 *              the page's cue to reload its lists.
 *   ref        { close() }: the page's own close (a drawer's ×, a header
 *              Cancel) -- nothing mid-request, the "saved without tubes"
 *              warning once the record exists, else onCancel.
 *   compact    inside a drawer that has its own heading: no heading or frame
 *   sourceTypes  the source types offered, of SOURCE_TYPES (default all the
 *              person may register): Home's "Add isolate / dish" offers bank
 *              stock alone under Preservation, and the others under
 *              External reception
 *   initialSource  a source type chosen from the start, when it is offered
 */

/** Who may put lab material on an intake: routes/plates.py and
 *  routes/slants.py's ELEVATED_ROLES (IntakeRecordDrawer's canCreateLabMaterial). */
const LAB_MATERIAL_ROLES = ['collection_manager', 'lab_technician', 'lab_manager'];

/** The fields BankStockFields reports errors for, cleared as any of it changes. */
const BANK_FIELDS = new Set(['slantCode', 'unit', 'preservedAt', 'preservedBy', 'organism']);
const isBankField = (f) => BANK_FIELDS.has(f) || f.startsWith('tubeWell');

/* A value that settles: the taxon name the medium is looked up for, once the
 * person stops typing -- not one request per keystroke. */
function useSettled(value, ms) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export default function IntakeForm({
  user, protocols = [], onDone, onCancel, onBusyChange, onSettled, compact = false, ref,
  sourceTypes = null, initialSource = '',
}) {
  const { t } = useTranslation(['external', 'collections', 'common', 'plates', 'slants', 'specimens']);
  const canCreateLabMaterial = !!user?.roles?.some((r) => LAB_MATERIAL_ROLES.includes(r));
  const options = sourcesFor(canCreateLabMaterial).filter((type) => !sourceTypes || sourceTypes.includes(type));

  const [today] = useState(localToday);
  const [sourceType, setSourceType] = useState(() => (options.includes(initialSource) ? initialSource : ''));
  // The code is made as the form opens, so it says when the intake began.
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, qr_uid: generateQrUid() }));
  const [condition, setCondition] = useState('fresh');
  const [plateSub, setPlateSub] = useState(EMPTY_PLATE_SUB);
  const [bankSub, setBankSub] = useState(() => defaultBankStock({ qrUid: form.qr_uid, userId: user?.user_id || '', today }));
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [showQr, setShowQr] = useState(false);
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  // Bank stock whose record was made but whose tubes were refused:
  // { external_id, specimen, warnings } -- from then on only the tubes are sent.
  const [saved, setSaved] = useState(null);
  const [tubesError, setTubesError] = useState('');
  const [tubesAttempt, setTubesAttempt] = useState(0);
  const errs = useFieldErrors();
  const headingRef = useRef(null);
  const suggestTaxon = useSettled(form.taxon_name.trim(), 600);

  // The latest callbacks, for a request that ends after a re-render -- or
  // after the form is gone, when only onSettled and onBusyChange are told.
  const props = useRef({});
  useEffect(() => { props.current = { onDone, onCancel, onBusyChange, onSettled }; });
  const mounted = useRef(false);
  const busy = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // A form taken away mid-request is no longer busy for its page.
      if (busy.current) {
        busy.current = false;
        props.current.onBusyChange?.(false);
      }
    };
  }, []);
  const setBusy = (value) => {
    busy.current = value;
    setSubmitting(value);
    props.current.onBusyChange?.(value);
  };
  // A medium typed as "Other" is added to the list once, however often the
  // tubes are sent.
  const registeredMedium = useRef(null);

  // On a page the form opens in place of the button that opened it, which
  // may be gone (an empty list's "record the first intake"): focus goes to
  // its heading. In a drawer the drawer's own heading takes it.
  useEffect(() => {
    if (!compact) focusElement(headingRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const source = SOURCES[sourceType];
  const subOf = (type) => (type === 'tissue' ? { condition } : type === 'culture' ? plateSub
    : type === 'bank_stock' ? bankSub : {});

  const chooseSource = (type) => {
    if (type === sourceType) return;
    setSourceType(type);
    // What is required follows the type; the donor rule does not.
    errs.clear((f) => f !== 'donor_name');
    if (type === 'bank_stock' && !bankSub.entries.some((en) => en.code.trim())) {
      setBankSub((s) => ({ ...s, entries: [{ code: tubeCode(form.qr_uid, 0), role: null, well: '' }] }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // The tubes' organism follows the taxon name until someone changes it --
    // and a message about it goes once it is filled that way -- and their
    // suggested codes follow the intake's code.
    if (name === 'taxon_name') {
      const before = form.taxon_name.trim();
      if (bankSub.organism.trim() === before) errs.clear('organism');
      setBankSub((s) => (s.organism.trim() === before ? { ...s, organism: value } : s));
    }
    if (name === 'qr_uid') {
      const before = form.qr_uid;
      setBankSub((s) => ({ ...s, entries: retagTubes(s.entries, before, value) }));
    }
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    // Either name satisfies "donor or original collector".
    errs.clear(name === 'original_collector' ? 'donor_name' : name);
  };

  /* POST /slants/external-intake for the record: null once the tubes are
   * banked, else why not, in the reader's words. */
  const sendTubes = async (externalId) => {
    const typed = protocolToRegister(bankSub);
    if (typed && typed !== registeredMedium.current) {
      registeredMedium.current = typed;
      api.post('/protocols', { name: typed }).catch(() => {});
    }
    try {
      await api.post('/slants/external-intake', bankStockBody(externalId, bankSub, user));
      return null;
    } catch (err) {
      return stepFailureReason(err, t, { bank: true });
    }
  };

  const refuseTubes = (message) => {
    setTubesError(message);
    setTubesAttempt((n) => n + 1);
  };

  // The record exists: from here on, only the tubes are sent.
  const storeTubes = async () => {
    const errors = bankStockErrors(bankSub, localToday());
    if (hasErrors(errors)) {
      errs.show(errors);
      return;
    }
    errs.reset();
    setBusy(true);
    const refusal = await sendTubes(saved.external_id);
    if (refusal === null) props.current.onSettled?.();
    if (!mounted.current) return;
    setBusy(false);
    if (refusal !== null) {
      refuseTubes(refusal);
      return;
    }
    props.current.onDone?.({ external_id: saved.external_id, specimen: saved.specimen, sourceType,
      warnings: saved.warnings });
  };

  // Closing with the record saved and its tubes not: said as the step that
  // failed, with how to bank them later.
  const closeWithoutTubes = () => {
    if (busy.current || !saved) return;
    props.current.onDone?.({ external_id: saved.external_id, specimen: saved.specimen, sourceType,
      warnings: [{ kind: 'step', message: t('source.stepFailed.bankStock', { error: tubesError }) }, ...saved.warnings] });
  };

  useImperativeHandle(ref, () => ({
    close: () => {
      if (busy.current) return;
      if (saved) closeWithoutTubes();
      else props.current.onCancel?.();
    },
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy.current) return;
    if (saved) {
      await storeTubes();
      return;
    }
    const sub = subOf(sourceType);
    const errors = sourceErrors(sourceType, form, sub, { today: localToday() });
    if (hasErrors(errors)) {
      setCreateError('');
      // A message in the folded description opens the fold, so focus can
      // reach its field and the message is seen.
      if (Object.keys(errors).some((f) => DESCRIPTION_FIELDS.includes(f))) setDescriptionOpen(true);
      errs.show(errors);
      return;
    }
    errs.reset();
    setBusy(true);
    setCreateError('');

    let created;
    try {
      created = (await api.post('/external', externalBody(sourceType, form, sub, user))).data || {};
    } catch (err) {
      if (!mounted.current) return;
      setCreateError(err.response?.data?.error || t('form.createFailed'));
      setBusy(false);
      return;
    }
    const externalId = created.external_id;
    const specimen = created.specimen || null;
    const warnings = [];

    // Photos first, so a failure further on does not lose them.
    if (pendingPhotos.length > 0) {
      let failed = [];
      try {
        ({ failed } = await uploadPhotos(pendingPhotos, { external_id: externalId }, form.taxon_name, 'external_collection'));
      } catch {
        failed = pendingPhotos;
      }
      if (failed.length > 0) {
        const names = failed.map((f) => String(f.photoType || '').replace(/_/g, ' ')).join(', ');
        warnings.push({ kind: 'photos', message: t('form.photoUploadFailed', { count: failed.length, names }) });
      }
    }

    if (sourceType === 'culture') {
      const typed = protocolToRegister(plateSub);
      if (typed) api.post('/protocols', { name: typed }).catch(() => {});
      try {
        await api.post('/plates', plateBody(externalId, plateSub));
      } catch (err) {
        warnings.unshift({ kind: 'step', message: t('source.stepFailed.plate', { error: stepFailureReason(err, t) }) });
      }
    } else if (sourceType === 'bank_stock') {
      const refusal = await sendTubes(externalId);
      if (refusal !== null) {
        props.current.onSettled?.();
        if (!mounted.current) return;
        setSaved({ external_id: externalId, specimen, warnings });
        refuseTubes(refusal);
        setBusy(false);
        return;
      }
    }

    props.current.onSettled?.();
    if (!mounted.current) return;
    setBusy(false);
    props.current.onDone?.({ external_id: externalId, specimen, sourceType, warnings });
  };

  const descriptionFields = (
    <div style={gridStyle}>
      <Field id="intake-substrate" field="substrate" label={t('form.substrateLabel')} error={errs.errors.substrate} t={t}>
        <select name="substrate" value={form.substrate} onChange={handleChange}
          {...fieldProps('intake-substrate', errs.errors.substrate)} title={t('tip.substrate')}>
          <option value="">{t('common:selectPlaceholder')}</option>
          {SUBSTRATES.map((s) => <option key={s} value={s}>{t(`collections:substrate.${s}`)}</option>)}
        </select>
      </Field>
      <Field id="intake-fruiting_body_size" field="fruiting_body_size" label={t('form.sizeLabel')} error={errs.errors.fruiting_body_size} t={t}>
        <select name="fruiting_body_size" value={form.fruiting_body_size} onChange={handleChange}
          {...fieldProps('intake-fruiting_body_size', errs.errors.fruiting_body_size)} title={t('tip.size')}>
          <option value="">{t('common:selectPlaceholder')}</option>
          {SIZES.map((s) => <option key={s} value={s}>{t(`collections:size.${s}`)} ({t(`collections:sizeHint.${s}`)})</option>)}
        </select>
      </Field>
      <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
        <input type="checkbox" name="fruiting_body_size_estimated" checked={form.fruiting_body_size_estimated}
          onChange={handleChange} title={t('tip.sizeEstimated')} />
        {t('form.sizeEstimateLabel')}
      </label>
      <Field id="intake-phenological_stage" field="phenological_stage" label={t('form.stageLabel')} error={errs.errors.phenological_stage} t={t}>
        <select name="phenological_stage" value={form.phenological_stage} onChange={handleChange}
          {...fieldProps('intake-phenological_stage', errs.errors.phenological_stage)} title={t('tip.stage')}>
          <option value="">{t('common:selectPlaceholder')}</option>
          {STAGES.map((s) => <option key={s} value={s}>{t(`collections:stage.${s}`)} ({t(`collections:stageHint.${s}`)})</option>)}
        </select>
      </Field>
      <Field id="intake-fruiting_body_count" field="fruiting_body_count" label={t('form.countLabel')} error={errs.errors.fruiting_body_count} t={t}>
        <input name="fruiting_body_count" type="number" min="1" step="1" value={form.fruiting_body_count} onChange={handleChange}
          {...fieldProps('intake-fruiting_body_count', errs.errors.fruiting_body_count)} title={t('tip.count')} />
      </Field>
      <Field id="intake-growth_pattern" field="growth_pattern" label={t('form.growthPatternLabel')} error={errs.errors.growth_pattern} t={t}>
        <select name="growth_pattern" value={form.growth_pattern} onChange={handleChange}
          {...fieldProps('intake-growth_pattern', errs.errors.growth_pattern)} title={t('tip.growthPattern')}>
          <option value="">{t('common:selectPlaceholder')}</option>
          {GROWTH_PATTERNS.map((g) => <option key={g} value={g}>{t(`collections:growthPattern.${g}`)}</option>)}
        </select>
      </Field>
    </div>
  );

  // The intake's code as the saved note names it: the one typed, else the
  // isolate's (the server makes its own EXT- code for a blank one).
  const savedCode = form.qr_uid.trim() || saved?.specimen?.specimen_code || '';

  return (
    // noValidate: every rule is said by sourceErrors beside its field. The
    // browser's own check would stop the submit silently on a field inside
    // the folded description, where its bubble cannot be seen.
    <form onSubmit={submit} noValidate style={compact ? undefined : formStyle} ref={errs.containerRef}
      data-testid="intake-form">
      {!compact && (
        <h3 ref={headingRef} tabIndex={-1} style={{ marginTop: 0 }} data-testid="intake-form-title">{t('form.title')}</h3>
      )}

      {saved ? (
        // The record is made; only the tubes are left. What was said about
        // the specimen is kept as sent, so none of it is shown to edit.
        <p role="status" style={savedNoteStyle} data-testid="intake-saved-note">
          {savedCode ? t('source.retry.saved', { code: savedCode }) : t('source.retry.savedNoCode')}
        </p>
      ) : (
        <>
          <p id="intake-source-legend" style={sectionHeading}>{t('source.legend')}</p>
          <p id="intake-source-hint" style={{ ...hintStyle, marginTop: -4 }}>{t('source.hint')}</p>
          <SourceChooser value={sourceType} options={options} onChoose={chooseSource} t={t}
            error={errs.errors.source} />
          <FieldError id="intake-source-error" testId="intake-source-error">{validationMessage(t, errs.errors.source)}</FieldError>
          {/* Some of the types this form offers are a lab writer's only. */}
          {options.length < SOURCE_TYPES.filter((type) => !sourceTypes || sourceTypes.includes(type)).length && (
            <p style={hintStyle} data-testid="intake-source-lab-only">{t('source.labOnlyHint')}</p>
          )}
        </>
      )}

      {source && (
        <>
          <p style={{ ...hintStyle, margin: '4px 0 14px' }}>{t('common:validation.requiredNote')}</p>

          {/* What arrived: the section the source type brings with it. */}
          {source.material === 'condition' && (
            <ConditionChoice value={condition} onChange={setCondition} t={t} />
          )}
          {source.material === 'plate' && (
            <section style={subFormStyle} aria-labelledby="intake-plate-heading" data-testid="intake-plate">
              <p id="intake-plate-heading" style={sectionHeading}>{t('source.plateHeading')}</p>
              <PlateFields value={plateSub} protocols={protocols} t={t} idPrefix="intake" errors={errs.errors}
                suggestTaxonName={suggestTaxon}
                onChange={(v) => { setPlateSub(v); errs.clear((f) => f === 'protocol' || f === 'protocolOther'); }} />
            </section>
          )}
          {source.material === 'bankStock' && (
            <section style={subFormStyle} aria-label={t(`source.types.${sourceType}.title`)}>
              {/* Why the tubes were refused, beside them; keyed by attempt so
                  the same refusal twice is announced twice. */}
              {tubesError && (
                <p key={tubesAttempt} role="alert" style={{ ...errStyle, fontSize: 14 }} data-testid="intake-tubes-error">
                  {tubesError}
                </p>
              )}
              <BankStockFields value={bankSub} protocols={protocols} currentUser={user} qrUid={form.qr_uid}
                today={today} t={t} idPrefix="intake" errors={errs.errors}
                onChange={(v) => { setBankSub(v); errs.clear(isBankField); }} />
            </section>
          )}

          {!saved && (
            <>
              <p style={sectionHeading}>{t('source.provenanceHeading')}</p>
              <div style={gridStyle}>
                <label style={labelStyle}>
                  {t('form.qrUidLabel')}
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input name="qr_uid" value={form.qr_uid} onChange={handleChange} style={{ ...inputStyle, flex: 1 }}
                      dir="ltr" title={t('tip.qrUid')} />
                    {form.qr_uid && (
                      <button type="button" onClick={() => setShowQr((s) => !s)} title={t('tip.showQr')} aria-expanded={showQr}
                        style={{ background: 'none', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '0 10px',
                          cursor: 'pointer', fontSize: 12, color: 'var(--c-text-fg)' }}>
                        {t('qrBtn')}
                      </button>
                    )}
                  </div>
                  <span style={{ ...hintStyle, margin: 0 }}>{t('form.autoGenHint')}</span>
                </label>
                {showQr && form.qr_uid && (
                  <div style={fullRow}><QRCodeLabel value={form.qr_uid} size={140} /></div>
                )}
                <Field id="intake-donor_name" field="donor_name" label={t('form.donorNameLabel')} error={errs.errors.donor_name} t={t}>
                  <input name="donor_name" value={form.donor_name} onChange={handleChange}
                    {...fieldProps('intake-donor_name', errs.errors.donor_name)} title={t('tip.donorName')} />
                </Field>
                <label style={labelStyle}>
                  {t('form.donorContactLabel')}
                  <input name="donor_contact" value={form.donor_contact} onChange={handleChange} style={inputStyle} title={t('tip.donorContact')} />
                </label>
                <label style={labelStyle}>
                  {t('form.originalCollectorLabel')}
                  {/* The other half of "donor or original collector": marked with the
                      donor field, and described by the same message. */}
                  <input name="original_collector" value={form.original_collector} onChange={handleChange}
                    {...invalidProps(errorIdFor('intake-donor_name'), !!errs.errors.donor_name)}
                    style={{ ...inputStyle, ...invalidBorder(!!errs.errors.donor_name) }} title={t('tip.originalCollector')} />
                </label>
                <label style={labelStyle}>
                  {t('form.originalCollectionDateLabel')}
                  <input name="original_collection_date" type="date" value={form.original_collection_date} onChange={handleChange}
                    style={inputStyle} title={t('tip.originalCollectionDate')} />
                </label>
                <label style={labelStyle}>
                  {t('form.locationNameLabel')}
                  <input name="original_location_name" value={form.original_location_name} onChange={handleChange}
                    style={inputStyle} title={t('tip.locationName')} />
                </label>
                <label style={labelStyle}>
                  {t('form.givenNameLabel')}
                  <input name="given_name" value={form.given_name} onChange={handleChange} maxLength={120} dir="auto"
                    style={inputStyle} title={t('tip.givenName')} data-testid="intake-given-name" />
                </label>
              </div>

              <MapGpsPicker
                lat={form.original_gps_lat}
                lng={form.original_gps_lng}
                hintText={t('form.gpsMapHint')}
                onChange={(lat, lng) => setForm((f) => ({ ...f, original_gps_lat: lat, original_gps_lng: lng }))}
                showRecapture={false}
              />

              <p style={sectionHeading}>{t('source.identificationHeading')}</p>
              <div style={gridStyle}>
                <label style={labelStyle}>
                  {t('form.taxonNameLabel')}
                  <input name="taxon_name" value={form.taxon_name} onChange={handleChange} style={inputStyle} dir="ltr" title={t('tip.taxonName')} />
                </label>
                <label style={labelStyle}>
                  {t('form.rankLabel')}
                  <select name="taxonomic_rank" value={form.taxonomic_rank} onChange={handleChange} style={inputStyle} title={t('tip.rank')}>
                    {RANKS.map((r) => <option key={r} value={r}>{t(`common:rank.${r}`)}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>
                  {t('form.confidenceLabel')}
                  <select name="confidence" value={form.confidence} onChange={handleChange} style={inputStyle} title={t('tip.confidence')}>
                    {CONFIDENCE.map((c) => <option key={c} value={c}>{t(`common:confidence.${c}`)}</option>)}
                  </select>
                </label>
              </div>

              {/* The specimen description: the whole record for a report, worth
                  having for a raw specimen, and folded away for material that is
                  already a culture or a tube -- opened when a field in it is wrong. */}
              {source.description === 'folded' ? (
                <details open={descriptionOpen} onToggle={(e) => setDescriptionOpen(e.currentTarget.open)}
                  style={{ marginBottom: 12 }} data-testid="intake-description-folded">
                  <summary title={t('source.tip.description')}
                    style={{ cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '6px 0' }}>
                    {t('source.descriptionFolded')}
                  </summary>
                  <p style={hintStyle}>{t('form.specimenDetailsOptionalHint')}</p>
                  {descriptionFields}
                </details>
              ) : (
                <>
                  <p style={{ ...sectionHeading, marginBottom: 2 }}>{t('form.specimenDetailsHeading')}</p>
                  <p style={hintStyle}>
                    {source.description === 'required' ? t('form.specimenDetailsRequiredHint') : t('form.specimenDetailsOptionalHint')}
                  </p>
                  {descriptionFields}
                </>
              )}

              <div style={gridStyle}>
                <label style={{ ...labelStyle, ...fullRow }}>
                  {t('form.generalNotesLabel')}
                  <textarea name="general_notes" value={form.general_notes} onChange={handleChange} rows={2} style={inputStyle}
                    title={t('tip.generalNotes')} />
                </label>
              </div>

              <PhotoPicker value={pendingPhotos} onChange={setPendingPhotos} />
            </>
          )}

          {createError && <p role="alert" style={{ ...errStyle, marginTop: 14 }} data-testid="intake-create-error">{createError}</p>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
            <button type="submit" disabled={submitting} style={btnStyle('var(--c-primary)')}
              title={saved ? t('source.tip.storeTubes') : t('tip.submitIntake')} data-testid="intake-submit">
              {submitting ? t('common:saving') : saved ? t('source.retry.storeTubes') : t('form.submitBtn')}
            </button>
            {saved ? (
              <button type="button" onClick={closeWithoutTubes} disabled={submitting} style={btnStyle('var(--c-text-muted)')}
                title={t('source.tip.closeWithoutTubes')} data-testid="intake-close-without-tubes">
                {t('source.retry.closeWithoutTubes')}
              </button>
            ) : onCancel && (
              <button type="button" onClick={onCancel} disabled={submitting} style={btnStyle('var(--c-text-muted)')}
                title={t('source.tip.cancel')}>
                {t('common:cancel')}
              </button>
            )}
          </div>
        </>
      )}
    </form>
  );
}

/* The note that the record is saved, in the theme's success colours: the
 * intake exists, and what is left is the tubes below it. */
const savedNoteStyle = {
  background: 'var(--c-success-soft)', color: 'var(--c-success-fg)', border: '1px solid var(--c-border)',
  borderRadius: 8, padding: '10px 14px', margin: '0 0 12px', fontSize: 14, fontWeight: 600,
};

/*
 * The source cards: one radio group, a card per type with its picture, what
 * it covers and where its isolate starts. Arrow keys move the choice (and
 * the focus) as in any radio group, mirrored on a right-to-left page; only
 * the chosen card -- or the first, before a choice -- is in the tab order.
 */
function SourceChooser({ value, options, onChoose, t, error }) {
  const refs = useRef({});
  const tabStop = options.includes(value) ? value : options[0];

  const onKeyDown = (e, type) => {
    const i = options.indexOf(type);
    const rtl = typeof getComputedStyle === 'function' && getComputedStyle(e.currentTarget).direction === 'rtl';
    const step = { ArrowDown: 1, ArrowUp: -1, ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[e.key];
    let next;
    if (step) next = (i + step + options.length) % options.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    else return;
    e.preventDefault();
    onChoose(options[next]);
    refs.current[options[next]]?.focus();
  };

  return (
    <div role="radiogroup" aria-labelledby="intake-source-legend"
      aria-describedby={error ? 'intake-source-hint intake-source-error' : 'intake-source-hint'}
      aria-required="true" aria-invalid={error ? 'true' : undefined} data-field="source" data-testid="intake-source"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 10,
        marginBottom: 12 }}>
      {options.map((type) => {
        const src = SOURCES[type];
        const checked = value === type;
        return (
          <button key={type} type="button" role="radio" aria-checked={checked} tabIndex={type === tabStop ? 0 : -1}
            ref={(el) => { refs.current[type] = el; }} onClick={() => onChoose(type)} onKeyDown={(e) => onKeyDown(e, type)}
            aria-labelledby={`intake-source-${type}-title`}
            aria-describedby={`intake-source-${type}-examples intake-source-${type}-stage`}
            title={t(`source.types.${type}.tip`)} data-testid={`intake-source-${type}`} style={cardStyle(checked)}>
            <span aria-hidden="true" style={{ fontSize: 24, lineHeight: 1 }}>{src.icon}</span>
            <span id={`intake-source-${type}-title`} style={{ fontSize: 14, fontWeight: 700 }}>
              {t(`source.types.${type}.title`)}
            </span>
            <span id={`intake-source-${type}-examples`} style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', lineHeight: 1.4 }}>
              {t(`source.types.${type}.examples`)}
            </span>
            <span id={`intake-source-${type}-stage`} style={stageBadge(src.stage)} data-testid={`intake-source-${type}-stage`}>
              {src.population
                ? t('source.startsAt', { stage: t(`source.stage.${src.stage}`) })
                : t(`source.stage.${src.stage}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* A raw specimen's condition: fresh, dried or in silica gel. A dried or
 * silica-gel specimen is recorded on the isolate as how it is kept. */
function ConditionChoice({ value, onChange, t }) {
  return (
    <fieldset data-field="condition" data-testid="intake-condition"
      style={{ ...subFormStyle, marginTop: 0, minWidth: 0 }}>
      <legend style={{ ...sectionHeading, padding: '0 4px', margin: 0 }}>{t('source.condition.legend')}</legend>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
        {TISSUE_CONDITIONS.map((c) => (
          <label key={c} className="touch-target" title={t(`source.condition.tip.${c}`)} style={pillStyle(value === c)}>
            <input type="radio" name="intake-condition" value={c} checked={value === c} onChange={() => onChange(c)}
              data-testid={`intake-condition-${c}`} />
            {t(`source.condition.${c}`)}
          </label>
        ))}
      </div>
      <p style={{ ...hintStyle, margin: '8px 0 0' }}>{t('source.condition.hint')}</p>
    </fieldset>
  );
}

const cardStyle = (checked) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, textAlign: 'start', minWidth: 0,
  padding: '12px 14px', borderRadius: 10, cursor: 'pointer', font: 'inherit', color: 'var(--c-text-fg)',
  background: checked ? 'var(--c-primary-soft)' : 'var(--c-surface)',
  border: `2px solid ${checked ? 'var(--c-primary)' : 'var(--c-border)'}`,
  boxShadow: checked ? '0 0 0 1px var(--c-primary)' : 'none',
});

/* Where the isolate starts, in the hub's own colours for those places. */
const STAGE_COLORS = {
  awaitingIsolation: { background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)' },
  labCulture: { background: 'var(--c-info-soft)', color: 'var(--c-info-fg)' },
  bank: { background: 'var(--c-success-soft)', color: 'var(--c-success-fg)' },
  noIsolate: { background: 'var(--c-bg-muted)', color: 'var(--c-text-muted-fg)' },
};
const stageBadge = (stage) => ({
  ...STAGE_COLORS[stage], marginTop: 'auto', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
});

const pillStyle = (checked) => ({
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
  fontSize: 14, fontWeight: checked ? 700 : 500, color: 'var(--c-text-fg)',
  background: checked ? 'var(--c-primary-soft)' : 'var(--c-surface)',
  border: `1px solid ${checked ? 'var(--c-primary)' : 'var(--c-border-strong)'}`,
});
